import { UserIcon } from '@heroicons/react/24/outline';
import axios from 'axios';
import { forwardRef, useImperativeHandle, useRef, useState } from 'react';

import { Button } from '@/components/core/Button';
import { Icon } from '@/components/core/Icon';
import { useToast } from '@/hooks/useToast';
import { useTranslation } from '@/hooks/useTranslation';
import { restUrl } from '@/services/utils';

const ACCEPTED_TYPES = ['image/jpeg', 'image/jpg', 'image/pjpeg', 'image/gif', 'image/png'];

interface CropBox {
    x: number;
    y: number;
    size: number;
}

interface ContactImageFieldProps {
    imageUrl?: string;
    readOnly: boolean;
    onChange: (imageUrl: string) => void;
}

interface ContactImageFieldHandle {
    uploadPending: () => Promise<string | undefined>;
}

const exportType = (fileType: string) => (fileType === 'image/jpeg' ? 'image/jpeg' : 'image/png');

const exportName = (fileType: string) => (fileType === 'image/jpeg' ? 'contact-image.jpg' : 'contact-image.png');

const ContactImageField = forwardRef<ContactImageFieldHandle, ContactImageFieldProps>(({
    imageUrl,
    readOnly,
    onChange,
}, ref) => {
    const { t } = useTranslation();
    const { toast } = useToast();
    const fileInputRef = useRef<HTMLInputElement>(null);
    const imageRef = useRef<HTMLImageElement>(null);
    const [source, setSource] = useState<string | undefined>();
    const [fileType, setFileType] = useState('image/jpeg');
    const [crop, setCrop] = useState<CropBox | undefined>();
    const [uploading, setUploading] = useState(false);

    const resetCrop = () => {
        setSource(undefined);
        setCrop(undefined);
        if (fileInputRef.current) {
            fileInputRef.current.value = '';
        }
    };

    const readFile = (file: File) => {
        if (!ACCEPTED_TYPES.includes(file.type)) {
            toast.error(t('UPLOAD.FILE_FORMAT_ERROR', { type: 'jpeg, gif, png' }));
            return;
        }
        setFileType(file.type === 'image/jpg' || file.type === 'image/pjpeg' ? 'image/jpeg' : file.type);
        const reader = new FileReader();
        reader.onload = () => {
            setSource(typeof reader.result === 'string' ? reader.result : undefined);
            setCrop(undefined);
        };
        reader.readAsDataURL(file);
    };

    const onImageLoad = () => {
        const image = imageRef.current;
        if (!image) {
            return;
        }
        const size = Math.min(image.naturalWidth, image.naturalHeight);
        setCrop({
            x: (image.naturalWidth - size) / 2,
            y: (image.naturalHeight - size) / 2,
            size,
        });
    };

    const onCropPointerDown = (event: React.PointerEvent<HTMLDivElement>) => {
        const image = imageRef.current;
        if (!image || !crop) {
            return;
        }
        event.preventDefault();
        const rect = image.getBoundingClientRect();
        const origin = { x: event.clientX, y: event.clientY, crop: { ...crop } };
        const move = (pointerEvent: PointerEvent) => {
            const dx = ((pointerEvent.clientX - origin.x) / rect.width) * image.naturalWidth;
            const dy = ((pointerEvent.clientY - origin.y) / rect.height) * image.naturalHeight;
            setCrop({
                x: Math.min(Math.max(0, origin.crop.x + dx), image.naturalWidth - origin.crop.size),
                y: Math.min(Math.max(0, origin.crop.y + dy), image.naturalHeight - origin.crop.size),
                size: origin.crop.size,
            });
        };
        const stop = () => {
            window.removeEventListener('pointermove', move);
            window.removeEventListener('pointerup', stop);
        };
        window.addEventListener('pointermove', move);
        window.addEventListener('pointerup', stop);
    };

    const uploadCrop = async (): Promise<string | undefined> => {
        if (!source) {
            return imageUrl;
        }
        const image = imageRef.current;
        if (!image || !crop || uploading) {
            toast.error(t('SAVE_ERROR_MESSAGE'));
            throw new Error('contact image is not ready');
        }
        setUploading(true);
        try {
            const canvas = document.createElement('canvas');
            canvas.width = 250;
            canvas.height = 250;
            const context = canvas.getContext('2d');
            const type = exportType(fileType);
            if (!context) {
                throw new Error('contact image canvas is unavailable');
            }
            context.drawImage(image, crop.x, crop.y, crop.size, crop.size, 0, 0, 250, 250);
            const blob = await new Promise<Blob | null>((resolve) => {
                canvas.toBlob(resolve, type);
            });
            if (!blob) {
                throw new Error('contact image export failed');
            }
            const body = new FormData();
            body.append('file', blob, exportName(fileType));
            const response = await axios.post<{ url?: string }[]>(restUrl('assets'), body);
            const url = response.data?.[0]?.url;
            if (!url) {
                throw new Error('contact image upload returned no url');
            }
            onChange(url);
            resetCrop();
            return url;
        } catch (error) {
            toast.error(t('SAVE_ERROR_MESSAGE'));
            throw error;
        } finally {
            setUploading(false);
        }
    };

    useImperativeHandle(ref, () => ({
        uploadPending: uploadCrop,
    }));

    const preview = source ?? imageUrl;

    return (
        <div className="tw-flex tw-flex-col tw-items-center tw-gap-3">
            {!source && (
                preview ? (
                    <img
                        src={preview}
                        alt=""
                        className="tw-h-24 tw-w-24 tw-rounded-full tw-object-cover lg:tw-h-28 lg:tw-w-28"
                    />
                ) : (
                    <span className="tw-flex tw-h-24 tw-w-24 tw-items-center tw-justify-center tw-rounded-full tw-bg-fill-secondary lg:tw-h-28 lg:tw-w-28">
                        <Icon icon={UserIcon} className="tw-h-10 tw-w-10 lg:tw-h-12 lg:tw-w-12" variant="secondary" />
                    </span>
                )
            )}
            {source && (
                <div className="tw-relative tw-inline-block">
                    <img
                        ref={imageRef}
                        src={source}
                        alt=""
                        onLoad={onImageLoad}
                        className="tw-block tw-max-h-52 tw-max-w-full"
                    />
                    {crop && imageRef.current && (
                        <div
                            role="presentation"
                            className="tw-absolute tw-cursor-move tw-border-2 tw-border-solid tw-border-primary-focus tw-bg-white/20"
                            style={{
                                left: `${(crop.x / imageRef.current.naturalWidth) * 100}%`,
                                top: `${(crop.y / imageRef.current.naturalHeight) * 100}%`,
                                width: `${(crop.size / imageRef.current.naturalWidth) * 100}%`,
                                height: `${(crop.size / imageRef.current.naturalHeight) * 100}%`,
                            }}
                            onPointerDown={onCropPointerDown}
                        />
                    )}
                </div>
            )}
            {!readOnly && (
                <div
                    className="tw-flex tw-flex-wrap tw-items-center tw-justify-center tw-gap-2"
                    onDragOver={(event) => event.preventDefault()}
                    onDrop={(event) => {
                        event.preventDefault();
                        const file = event.dataTransfer.files?.[0];
                        if (file) {
                            readFile(file);
                        }
                    }}
                >
                    <input
                        ref={fileInputRef}
                        type="file"
                        accept="image/jpeg,image/gif,image/png"
                        className="tw-sr-only"
                        onChange={(event) => {
                            const file = event.target.files?.[0];
                            if (file) {
                                readFile(file);
                            }
                        }}
                    />
                    {!source && (
                        <Button type="button" variant="secondary" onClick={() => fileInputRef.current?.click()}>
                            {imageUrl ? t('UPLOAD.CHANGE_IMAGE') : t('CHOOSE_IMAGE')}
                        </Button>
                    )}
                    {source && (
                        <>
                            <Button type="button" onClick={() => { void uploadCrop().catch(() => undefined); }} disabled={!crop || uploading} loading={uploading}>
                                {t('UPLOAD_IMAGE')}
                            </Button>
                            <Button type="button" variant="secondary" onClick={resetCrop} disabled={uploading}>
                                {t('CANCEL')}
                            </Button>
                        </>
                    )}
                </div>
            )}
        </div>
    );
});

ContactImageField.displayName = 'ContactImageField';

export { ContactImageField };
export type { ContactImageFieldHandle };
